import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import { resolveProductOpenInNewTab } from '@/config/productConfig';
import type {
  CreateProductRequest,
  Product,
  UpdateProductRequest,
} from '@/types/product';

interface ProductResponseDto {
  product_id: string;
  name_key: string;
  description_key?: string | null;
  iframe_url: string;
  icon?: string | null;
  is_active?: boolean;
  sort_order?: number;
  open_in_new_tab?: boolean;
}

interface ProductCreateDto {
  product_id: string;
  iframe_url: string;
  name_key?: string | null;
  description_key?: string | null;
  icon?: string | null;
  is_active?: boolean;
  sort_order?: number;
  open_in_new_tab?: boolean;
}

interface ProductUpdateDto {
  name_key?: string | null;
  description_key?: string | null;
  iframe_url?: string | null;
  icon?: string | null;
  is_active?: boolean | null;
  sort_order?: number | null;
  open_in_new_tab?: boolean | null;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readRequiredString(value: unknown, field: string): string {
  if (typeof value !== 'string') {
    throw new Error(`Invalid product API response: ${field} must be a string.`);
  }

  return value;
}

function readOptionalString(
  value: unknown,
  field: string,
): string | null | undefined {
  if (value === undefined || value === null) {
    return value as null | undefined;
  }

  if (typeof value !== 'string') {
    throw new Error(
      `Invalid product API response: ${field} must be a string when provided.`,
    );
  }

  return value;
}

function readOptionalBoolean(
  value: unknown,
  field: string,
): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value !== 'boolean') {
    throw new Error(
      `Invalid product API response: ${field} must be a boolean when provided.`,
    );
  }

  return value;
}

function readOptionalNumber(value: unknown, field: string): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(
      `Invalid product API response: ${field} must be a finite number when provided.`,
    );
  }

  return value;
}

function toProductResponseDto(value: unknown): ProductResponseDto {
  if (!isRecord(value)) {
    throw new Error('Invalid product API response: payload must be an object.');
  }

  return {
    product_id: readRequiredString(value.product_id, 'product_id'),
    name_key: readRequiredString(value.name_key, 'name_key'),
    description_key: readOptionalString(
      value.description_key,
      'description_key',
    ),
    iframe_url: readRequiredString(value.iframe_url, 'iframe_url'),
    icon: readOptionalString(value.icon, 'icon'),
    is_active: readOptionalBoolean(value.is_active, 'is_active'),
    sort_order: readOptionalNumber(value.sort_order, 'sort_order'),
    open_in_new_tab: readOptionalBoolean(
      value.open_in_new_tab,
      'open_in_new_tab',
    ),
  };
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function mapProductDto(dto: ProductResponseDto): Product {
  return {
    id: dto.product_id,
    name: dto.name_key,
    description: dto.description_key,
    iframeUrl: dto.iframe_url,
    icon: dto.icon,
    isActive: dto.is_active,
    sortOrder: dto.sort_order,
    openInNewTab: resolveProductOpenInNewTab(
      dto.product_id,
      dto.open_in_new_tab,
    ),
  };
}

function toCreateProductDto(data: CreateProductRequest): ProductCreateDto {
  return {
    product_id: data.id?.trim() || slugify(data.name),
    iframe_url: data.iframeUrl,
    name_key: data.name,
    description_key: data.description ?? null,
    icon: data.icon ?? null,
    is_active: data.isActive ?? true,
    sort_order: data.sortOrder ?? 0,
    open_in_new_tab: data.openInNewTab ?? false,
  };
}

function toUpdateProductDto(data: UpdateProductRequest): ProductUpdateDto {
  return {
    name_key: data.name,
    description_key: data.description,
    iframe_url: data.iframeUrl,
    icon: data.icon,
    is_active: data.isActive,
    sort_order: data.sortOrder,
    open_in_new_tab: data.openInNewTab,
  };
}

export async function getProducts(): Promise<Product[]> {
  const response = await apiClient.get<unknown>(endpoints.products);

  if (!Array.isArray(response.data)) {
    throw new Error(
      'Invalid product API response: products payload must be an array.',
    );
  }

  return response.data.map((item) => mapProductDto(toProductResponseDto(item)));
}

export async function getProduct(productId: string): Promise<Product> {
  const response = await apiClient.get<unknown>(
    endpoints.productById(productId),
  );

  return mapProductDto(toProductResponseDto(response.data));
}

export async function createProduct(
  data: CreateProductRequest,
): Promise<Product> {
  const response = await apiClient.post<unknown>(
    endpoints.products,
    toCreateProductDto(data),
  );

  return mapProductDto(toProductResponseDto(response.data));
}

export async function updateProduct(
  productId: string,
  data: UpdateProductRequest,
): Promise<Product> {
  const response = await apiClient.patch<unknown>(
    endpoints.productById(productId),
    toUpdateProductDto(data),
  );

  return mapProductDto(toProductResponseDto(response.data));
}

export async function deleteProduct(productId: string): Promise<void> {
  await apiClient.delete(endpoints.productById(productId));
}
